import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useRequest } from 'ahooks'
import clsx from 'clsx'

import CheckmarkColorfulIcon from '$commonSrc/checkmark-colorful.svg'
import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonIcon,
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import {
  privacyPolicyUrl,
  tosUrl,
  googleOauthPath,
  validateSignupEmailPath,
  checkIsEmailEnterpriseSSOPath,
} from '$extensionSrc/consts/urlConsts'
import { getEnabledPlatforms } from '$extensionSrc/data/enabledPlatforms'
import useForm from '$extensionSrc/hooks/useForm'
import { isValidEmail } from '$extensionSrc/utils/stringValidator'
import { authFetch } from '$serverSrc/utils/fetch'
import SignupAsset from '$tenantSrc/signup-asset.svg'

import ContinueWithGoogle from './ContinueWithGoogle'
import FormTextInput from './FormTextInput'
import styles from './SignupPage.module.scss'

function SignupFeatureRow({ children }: PropsWithChildren) {
  return (
    <div className="SignupFeatureRow">
      <CheckmarkColorfulIcon aria-hidden className={styles.icon} />
      <div className="body2">{children}</div>
    </div>
  )
}

export default function SignupPage({
  code,
  initialError,
  percentOff,
}: {
  code: string | null
  initialError: string
  percentOff: number | null
}) {
  const cfIpcountry: string | null = useMemo(() => {
    let url: URL | undefined
    try {
      url = new URL(window.location.href)
    } catch {}
    return url?.searchParams.get('cf_ipcountry') || null
  }, [])

  const [mobileEmailExpanded, setMobileEmailExpanded] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isEnterpriseSSO, setIsEnterpriseSSO] = useState<boolean | null>(false)

  const defaultValues = { email: '' }
  const requiredValidations = {
    email: (x: string) => isValidEmail(x),
  }
  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const redirectUrl = useMemo(() => {
    const usp = new URLSearchParams({ action: 'signup' })
    if (cfIpcountry) {
      usp.set('cf_ipcountry', cfIpcountry)
    }
    return `${googleOauthPath}?${usp.toString()}`
  }, [cfIpcountry])

  const { run: submitEmail } = useRequest(
    (email) => {
      const formData = new FormData()
      formData.append('email', email)

      return authFetch(
        new Request(validateSignupEmailPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      throttleWait: 500,
      throttleTrailing: false,
      onSuccess: (response: Response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.signup_url) {
              window.location.assign(data.signup_url)
            }
          } else {
            setEmailError(data.error)
          }
        })
      },
    },
  )

  const { run: getIsEmailEnterpriseSSO } = useRequest(
    () => {
      const formData = new FormData()
      formData.append('email', values.email)

      return authFetch(
        new Request(checkIsEmailEnterpriseSSOPath, {
          method: 'POST',
          body: formData,
        }),
      )
    },
    {
      manual: true,
      debounceWait: 300,
      onSuccess: (response: Response) => {
        response.json().then((data) => {
          if (response.status === 200) {
            if (data.is_enterprise_sso) {
              setIsEnterpriseSSO(true)
            } else {
              setIsEnterpriseSSO(false)
            }
          } else {
            setIsEnterpriseSSO(false)
          }
        })
      },
    },
  )

  useEffect(() => {
    if (isValid) {
      getIsEmailEnterpriseSSO()
    }
  }, [values.email, isValid, getIsEmailEnterpriseSSO])

  const banner: ReactNode = useMemo(() => {
    // By code
    if (code === 'YAHOOPILOT') {
      return 'Sign in with Okta to activate your Yahoo-sponsored access ($99 per year value)'
    }

    // General catch all
    if (code) {
      if (percentOff === 100) {
        return 'Get your first year free using your promo code'
      }
      return `Get your first year for ${percentOff}% off with promo code ${code}`
    }

    return undefined
  }, [code, percentOff])

  const numPlatformsSupported = useMemo(() => getEnabledPlatforms().length, [])

  return (
    <div className="SignupPage">
      <div className={clsx(styles.bannerMobileTop, 'body2', 'black', 'center')}>
        {banner}
      </div>
      <div className={styles.marketingSection}>
        <div className={styles.marketingHeader}>
          Deep clean your social media with just one click
        </div>
        <div className={styles.featureListContent}>
          <SignupFeatureRow>
            <span className="bold purple">Monthly scans</span> for ongoing
            protection ({numPlatformsSupported}+ platforms)
          </SignupFeatureRow>
          <SignupFeatureRow>
            <span className="bold purple">1-click</span> fixes to remediate
            privacy and security risks
          </SignupFeatureRow>
          <SignupFeatureRow>
            <span className="bold purple">Automated tools</span> for content and
            connection cleanup
          </SignupFeatureRow>
        </div>
        <SignupAsset
          aria-label="Extension dashboard with todos for supported platforms, including Facebook, Twitter Instagram and more"
          className={styles.primaryIllustration}
        />

        <div className={styles.mobileOnlySpacer}>
          {/*
           * Not all marketing content shows on Chrome & Safari Mobile.
           * We add a spacer to ensure it always shows.
           */}
        </div>
      </div>
      <div
        className={clsx(
          styles.signupOptionsContainer,
          mobileEmailExpanded && styles.expanded,
        )}
      >
        <div className={clsx(styles.banner, 'body1', 'black', 'center')}>
          {banner}
        </div>
        <div className={styles.signupOptionsContent}>
          {emailError && (
            <Toast text={emailError} noAutoDismiss showCloseIcon color="red" />
          )}
          <div className={styles.buttonsContainer}>
            <div
              className={clsx(styles.desktopOnly, 'heading1', 'bold', 'center')}
            >
              Sign up for {BuildConfig.TENANT_NAME}
            </div>
            <div
              className={clsx(
                styles.mobileOnly,
                'heading4',
                'bold',
                'center',
                mobileEmailExpanded && styles.hidden,
              )}
            >
              Sign up for {BuildConfig.TENANT_NAME} with:
            </div>
            <div className={styles.desktopOnly}>
              <ContinueWithGoogle redirectUrl={redirectUrl} />
            </div>
            <div
              className={clsx(styles.mobileOnly, styles.mobileSignupButtons)}
            >
              <ContinueWithGoogle redirectUrl={redirectUrl} text="Google" />
              <ActionButton
                className={clsx(
                  styles.emailButton,
                  styles.override,
                  styles.colorOverride,
                  mobileEmailExpanded && styles.lightestPurple,
                )}
                type={ActionButtonType.SECONDARY}
                size={ActionButtonSize.SMALL}
                role="link"
                onClick={() => setMobileEmailExpanded(!mobileEmailExpanded)}
                iconName={ActionButtonIcon.EMAIL}
              >
                Email
              </ActionButton>
            </div>
            <div className={clsx(styles.desktopOnly)}>
              <div className="or-text body1 bold center">or</div>
            </div>
            <form
              className={clsx(
                styles.emailSection,
                mobileEmailExpanded && styles.mobileExpanded,
              )}
              onSubmit={(e) => {
                e.preventDefault()
                submitEmail(values.email)
              }}
            >
              <FormTextInput
                placeholderText="Email address"
                name="email"
                isSensitive={false}
                value={values.email}
                handleChange={(value) => {
                  getFieldChangeHandler('email')(value)
                }}
                requirements={[
                  {
                    text: 'Enter a valid email address',
                    isFulfilled: getRequiredValidationMetByField('email'),
                  },
                ]}
                infoText="Use your work email to redeem sponsored access"
              />
              <ActionButton
                className={clsx(
                  styles.emailButton,
                  styles.override,
                  styles.colorOverride,
                  !isValid && styles.disabled,
                )}
                htmlType="submit"
                type={ActionButtonType.SECONDARY}
                size={ActionButtonSize.SMALL}
                role="link"
                iconName={
                  isEnterpriseSSO
                    ? ActionButtonIcon.GENERAL_OAUTH
                    : ActionButtonIcon.EMAIL
                }
                disabled={!isValid}
              >
                <span className={styles.desktopOnly}>
                  {isEnterpriseSSO
                    ? 'Continue with your employer'
                    : 'Continue with email'}
                </span>
                <span className={styles.mobileOnly}>
                  {isEnterpriseSSO ? 'Continue with your employer' : 'Continue'}
                </span>
              </ActionButton>
            </form>
          </div>
          <div className={clsx(styles.disclaimer)}>
            By continuing, you acknowledge that you understand and agree to
            {BuildConfig.TENANT_NAME}'s{' '}
            <a className="link2" href={tosUrl} target="_blank" rel="noreferrer">
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a
              className="link2"
              href={privacyPolicyUrl}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </div>
      {initialError && (
        <Toast text={initialError} noAutoDismiss showCloseIcon color="red" />
      )}
    </div>
  )
}
